import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { GiFairyWings } from 'react-icons/gi';
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaPaperPlane,
  FaRedo,
} from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import "./Contact.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    comment: "",
  });

  const [captchaText, setCaptchaText] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [loading, setLoading] = useState(false);

  // Generate an alphanumeric CAPTCHA code
  const generateCaptcha = () => {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < 6; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    setCaptchaText(result);
    setCaptchaInput("");
  };

  // Generate initial CAPTCHA on component load
  useEffect(() => {
    generateCaptcha();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Mobile Validation
    if (name === "phone") {
      const input = value.replace(/\D/g, "");

      if (input.length > 0 && !["6", "7", "8", "9"].includes(input[0])) {
        return;
      }

      setFormData({
        ...formData,
        phone: input.slice(0, 10),
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Verify Captcha
    if (captchaInput !== captchaText) {
      Swal.fire({
        title: "Invalid CAPTCHA",
        text: "The security code does not match. Please try again.",
        icon: "error",
        confirmButtonColor: "#F37936",
      });
      generateCaptcha();
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/contact/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        Swal.fire({
          title: "Submitted!",
          text: "Your message has been sent successfully.",
          icon: "success",
          confirmButtonColor: "#F37936",
        });

        setFormData({
          name: "",
          phone: "",
          email: "",
          comment: "",
        });
        generateCaptcha();
      } else {
        Swal.fire(
          "Error!",
          data.message || "Something went wrong.",
          "error"
        );
        generateCaptcha();
      }
    } catch (err) {
      Swal.fire(
        "Server Error!",
        "Could not connect to the server.",
        "error"
      );
      generateCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Rajput Shiksha Kosh</title>

        <meta
          name="description"
          content="Contact us for donation, support, and inquiries."
        />
      </Helmet>

      <div className="contact-us-page-wrapper">
        <div className="contact-us-page-container">

          {/* HEADER */}
          <div className="contact-us-page-header">
            <h1 className="contact-us-page-title">CONTACT US</h1>

            <div className="contact-us-page-divider">
              <span className="contact-us-page-divider-line"></span>

              <GiFairyWings className="contact-us-page-divider-icon" />

              <span className="contact-us-page-divider-line"></span>
            </div>
          </div>

          {/* MAIN GRID */}
          <div className="contact-us-page-grid">

            {/* LEFT SIDE */}
            <div className="contact-us-page-info-card">
              <h2 className="contact-us-page-info-title">
                Get in Touch
              </h2>

              <p className="contact-us-page-info-desc">
                For any donation related queries, support, or general
                information, feel free to contact us anytime.
              </p>

              <div className="contact-us-page-info-list">

                <div className="contact-us-page-info-item">
                  <div className="contact-us-page-icon-box">
                    <FaMapMarkerAlt />
                  </div>

                  <div className="contact-us-page-info-text">
                    <h5>Address</h5>

                    <p>
                      Marwar Rajput Sabha Bhawan,
                      <br />
                      Paota 'B' Road, Jodhpur(342001)
                    </p>
                  </div>
                </div>

                <div className="contact-us-page-info-item">
                  <div className="contact-us-page-icon-box">
                    <FaPhoneAlt />
                  </div>

                  <div className="contact-us-page-info-text">
                    <h5>Phone Number</h5>

                    <p>+91-9414133996</p>
                    <p>+91-9829266066</p>
                  </div>
                </div>

                <div className="contact-us-page-info-item">
                  <div className="contact-us-page-icon-box">
                    <GiFairyWings />
                  </div>

                  <div className="contact-us-page-info-text">
                    <h5>Email Address</h5>

                    <p> rajputshikhshakosh@gmail.com</p>
                  </div>
                </div>

              </div>
            </div>

            {/* RIGHT SIDE FORM */}
            <div className="contact-us-page-form-card">

              <h3 className="contact-us-page-form-title">
                Send Message
              </h3>

              <form
                className="contact-us-page-form"
                onSubmit={handleSubmit}
              >

                <div className="contact-us-page-input-group">
                  <label>Full Name</label>

                  <input
                    type="text"
                    name="name"
                    placeholder="Enter Full Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="contact-us-page-input-group">
                  <label>Contact Number</label>

                  <div className="contact-us-page-phone-wrapper">
                    <span className="contact-us-page-country-code">
                      +91
                    </span>

                    <input
                      type="text"
                      name="phone"
                      placeholder="Enter Mobile Number"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="contact-us-page-input-group">
                  <label>Email Address</label>

                  <input
                    type="email"
                    name="email"
                    placeholder="Enter Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="contact-us-page-input-group">
                  <label>Comment</label>

                  <textarea
                    rows="5"
                    name="comment"
                    placeholder="Write your message here..."
                    value={formData.comment}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                </div>

                {/* CAPTCHA SECTION */}
                <div className="contact-us-page-input-group">
                  <label>Security Code (CAPTCHA)</label>
                  
                  <div className="contact-us-page-captcha-display-container">
                    <div className="contact-us-page-captcha-code-box">
                      {captchaText}
                    </div>
                    
                    <button
                      type="button"
                      className="contact-us-page-captcha-refresh-btn"
                      onClick={generateCaptcha}
                      title="Refresh CAPTCHA"
                    >
                      <FaRedo />
                    </button>
                  </div>

                  <input
                    type="text"
                    name="captcha"
                    placeholder="Enter the code shown above"
                    value={captchaInput}
                    onChange={(e) => setCaptchaInput(e.target.value)}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="contact-us-page-submit-btn"
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Submit Message"}

                  <FaPaperPlane />
                </button>

              </form>
            </div>
          </div>

          {/* GOOGLE MAP SECTION */}
          <div className="contact-us-page-map-container">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3576.9004451428373!2d73.0429466!3d26.297327199999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39418d07196144d9%3A0xffc9155891dd8342!2sRajput%20Shiksha%20Kosh!5e0!3m2!1sen!2sin!4v1780125346895!5m2!1sen!2sin"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Rajput Shiksha Kosh Map"
            ></iframe>
          </div>

        </div>
      </div>
    </>
  );
}

export default Contact;